import React from 'react';
import { PALETTES } from '../../theme/palette';
import { MIN_USER_SCALE, MAX_USER_SCALE } from '../../theme/uiScale';
import Slider from './Slider';
import VoiceCard from './VoiceCard';

export interface AudioSettings {
  master: number;
  effects: number;
  footsteps: number;
  voice: number;
  music: number;
}

const VOLUME_SLIDERS: Array<{ key: keyof AudioSettings; label: string }> = [
  { key: 'master', label: 'Volume général' },
  { key: 'effects', label: 'Effets' },
  { key: 'footsteps', label: 'Pas' },
  { key: 'voice', label: 'Voix' },
  { key: 'music', label: 'Musique' },
];

interface Props {
  palette: string;
  reduceAnimations: boolean;
  hungerPersistent: boolean;
  drunkPersistent: boolean;
  audio: AudioSettings | null;
  uiScale: number;
  notificationVolume: number;
  onSelectPalette: (id: string) => void;
  onToggleReduceAnimations: (value: boolean) => void;
  onToggleHungerPersistent: (value: boolean) => void;
  onToggleDrunkPersistent: (value: boolean) => void;
  onOpenGameSettings: () => void;
  onSetVolume: (key: keyof AudioSettings, value: number) => void;
  onSetUiScale: (value: number) => void;
  onSetNotificationVolume: (value: number) => void;
}

const OptionsPage: React.FC<Props> = ({
  palette, reduceAnimations, hungerPersistent, drunkPersistent, audio, uiScale, notificationVolume,
  onSelectPalette, onToggleReduceAnimations, onToggleHungerPersistent, onToggleDrunkPersistent, onOpenGameSettings, onSetVolume, onSetUiScale, onSetNotificationVolume,
}) => {
  return (
    <div className="esc-options">
      <section className="esc-card">
        <h3 className="esc-card__title">Son</h3>
        <div className="esc-sliders">
          {VOLUME_SLIDERS.map((s) => (
            <Slider
              key={s.key}
              id={`esc-vol-${s.key}`}
              label={s.label}
              value={audio ? audio[s.key] : 1}
              disabled={!audio}
              display={audio ? `${Math.round(audio[s.key] * 100)} %` : '...'}
              onChange={(v) => onSetVolume(s.key, v)}
            />
          ))}
          <Slider
            id="esc-vol-notifications"
            label="Notifications"
            value={notificationVolume}
            display={`${Math.round(notificationVolume * 100)} %`}
            onChange={onSetNotificationVolume}
          />
        </div>
      </section>

      <VoiceCard />

      <section className="esc-card">
        <h3 className="esc-card__title">Interface</h3>
        <div className="esc-sliders">
          <Slider
            id="esc-ui-scale"
            label="Taille"
            value={uiScale}
            min={MIN_USER_SCALE}
            max={MAX_USER_SCALE}
            display={`${Math.round(uiScale * 100)} %`}
            onChange={onSetUiScale}
          />
        </div>
        <p className="esc-hint">S'ajoute à l'ajustement automatique selon la résolution de ton écran.</p>

        <div className="esc-setting">
          <div className="esc-setting__text">
            <span className="esc-setting__label" id="esc-reduce-label">Réduire les animations</span>
            <span className="esc-setting__hint">Remplace les transitions des menus par de simples fondus.</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={reduceAnimations}
            aria-labelledby="esc-reduce-label"
            className={'esc-switch' + (reduceAnimations ? ' esc-switch--on' : '')}
            onClick={() => onToggleReduceAnimations(!reduceAnimations)}
          >
            <span className="esc-switch__knob" />
          </button>
        </div>

        <div className="esc-setting">
          <div className="esc-setting__text">
            <span className="esc-setting__label" id="esc-hunger-label">Jauge de faim toujours visible</span>
            <span className="esc-setting__hint">Désactivé, elle n'apparaît que lorsque tu as faim.</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={hungerPersistent}
            aria-labelledby="esc-hunger-label"
            className={'esc-switch' + (hungerPersistent ? ' esc-switch--on' : '')}
            onClick={() => onToggleHungerPersistent(!hungerPersistent)}
          >
            <span className="esc-switch__knob" />
          </button>
        </div>

        <div className="esc-setting">
          <div className="esc-setting__text">
            <span className="esc-setting__label" id="esc-drunk-label">Jauge d'ivresse toujours visible</span>
            <span className="esc-setting__hint">Désactivé, elle n'apparaît que lorsque tu es ivre.</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={drunkPersistent}
            aria-labelledby="esc-drunk-label"
            className={'esc-switch' + (drunkPersistent ? ' esc-switch--on' : '')}
            onClick={() => onToggleDrunkPersistent(!drunkPersistent)}
          >
            <span className="esc-switch__knob" />
          </button>
        </div>

        <div className="esc-setting">
          <div className="esc-setting__text">
            <span className="esc-setting__label">Paramètres du jeu</span>
            <span className="esc-setting__hint">Ouvre le menu d'options natif de Skyrim : vidéo, contrôles.</span>
          </div>
          <button type="button" className="esc-btn" onClick={onOpenGameSettings}>
            Ouvrir
          </button>
        </div>
      </section>

      <section className="esc-card esc-options__palette">
        <h3 className="esc-card__title">Palette</h3>
        <p className="esc-hint">S'applique tout de suite à toutes les fenêtres et est conservée entre les sessions.</p>
        <div className="esc-palettes" role="radiogroup" aria-label="Palette de l'interface">
          {PALETTES.map((p) => (
            <button
              type="button"
              key={p.id}
              role="radio"
              aria-checked={p.id === palette}
              className={'esc-palette' + (p.id === palette ? ' esc-palette--active' : '')}
              onClick={() => onSelectPalette(p.id)}
            >
              <span className="esc-palette__swatches" aria-hidden="true">
                {p.swatches.map((color, i) => (
                  <span key={i} className="esc-palette__swatch" style={{ background: color }} />
                ))}
              </span>
              <span className="esc-palette__label">{p.label}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default OptionsPage;
